/**
 * DocumentCaptureModule — 3-tab capture surface (Scanner / Camera / Upload).
 *
 * Ported from legacy SPFx `shared/DocumentCaptureModule.tsx` (650 lines):
 *   - ScannerTab: lines 194-275  → v3 ScannerTab (uses useTwainBridge)
 *   - CameraTab:  lines 279-359  → v3 CameraTab  (getUserMedia + canvas)
 *   - UploadTab:  lines 363-435  → v3 UploadTab  (drag-drop + FileReader)
 *   - Header/tabs lines 439-569  → v3 Tabs + tab content router
 *
 * v3 adaptations:
 *   - Scanner tab auto-hides on mobile UA (UA check in useTwainBridge).
 *   - Upload tab size cap is 4 MB (Azure DI hard limit) instead of legacy 25 MB.
 *   - No SpListService.uploadFileToLibrary call here — that's IdCapturePanel's
 *     responsibility after extraction so the saved file metadata can include
 *     the extracted ID type.
 *   - Uses PCB brand tokens + `title`/`aria` attributes for accessibility.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties, type JSX, type DragEvent, type ChangeEvent } from 'react';
import { TwainBridgeStatus } from './TwainBridgeStatus.js';
import { SCAN_RECOVERY_ADVICE, useTwainBridge, type UseTwainBridgeResult } from '../../hooks/useTwainBridge.js';
import { preflightImageFile } from '../../utils/imageConvert';

export type CaptureTab = 'scanner' | 'camera' | 'upload';

export interface DocumentCaptureModuleProps {
  onCapture: (base64: string, mimeType: string) => void;
  /** Restrict visible tabs. Defaults to all 3 — scanner auto-hides on mobile. */
  tabs?: ReadonlyArray<CaptureTab>;
  /** Pre-wired TWAIN bridge (share state with IdCapturePanel so the hook isn't
   *  duplicated). When absent, DocumentCaptureModule creates its own. */
  twain?: UseTwainBridgeResult;
  /** Max upload size in bytes. Default 4 MB (Azure DI hard cap). */
  maxUploadBytes?: number;
}

// ---------------------------------------------------------------------------
// Shared styles (PCB brand)
// ---------------------------------------------------------------------------

const SHELL_STYLE = {
  border: '1px solid var(--surface-border)',
  borderRadius: 8,
  overflow: 'hidden',
  fontFamily: "'Segoe UI', Roboto, sans-serif",
  fontSize: 13,
  color: 'var(--ink-800)',
  background: 'var(--surface-card)',
} as const;

const TAB_BAR_STYLE = {
  display: 'flex',
  borderBottom: '2px solid var(--surface-border)',
  background: 'var(--surface-subtle)',
} as const;

function tabStyle(active: boolean): CSSProperties {
  return {
    padding: '8px 16px',
    fontSize: 12,
    fontWeight: active ? 700 : 500,
    color: active ? 'var(--pcb-blue)' : 'var(--ink-600)',
    background: active ? 'var(--surface-card)' : 'transparent',
    border: 'none',
    borderBottom: active ? '2px solid var(--pcb-blue)' : '2px solid transparent',
    marginBottom: -2,
    cursor: 'pointer',
    fontFamily: "'Segoe UI', Roboto, sans-serif",
    transition: 'color 0.15s',
  };
}

const BODY_STYLE = { padding: 14 } as const;

const BTN_PRIMARY: CSSProperties = {
  padding: '7px 16px',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  border: 'none',
  background: 'var(--brand-fill)',
  color: 'var(--on-brand)',
  fontFamily: "'Segoe UI', Roboto, sans-serif",
};

const BTN_DANGER: CSSProperties = { ...BTN_PRIMARY, background: 'var(--err-fill)' };

const ERROR_STYLE: CSSProperties = {
  fontSize: 12,
  padding: '6px 10px',
  borderRadius: 4,
  marginTop: 8,
  background: 'var(--err-bg)',
  color: 'var(--err-text)',
};

// ---------------------------------------------------------------------------
// Scanner tab
// ---------------------------------------------------------------------------

interface ScannerTabProps {
  twain: UseTwainBridgeResult;
  onCapture: (base64: string, mimeType: string) => void;
}

function ScannerTab({ twain, onCapture }: ScannerTabProps): JSX.Element {
  const [scanning, setScanning] = useState<boolean>(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const sources = twain.status.sources ?? [];
  const selected = twain.status.selectedSource ?? sources[0] ?? '';

  const handleScan = async (): Promise<void> => {
    setScanning(true);
    setScanError(null);
    try {
      const result = await twain.scan(selected);
      if (!result) {
        // CHG-0102: show the bridge's own reason. A generic "no image" here sent officers to re-seat the ID when
        // the bridge had actually lost its scanner session.
        const why = twain.lastScanError() ?? 'Scan returned no image. Verify the ID is on the platen and try again.';
        setScanError(why.includes('Upload or Camera') ? why : `${why} ${SCAN_RECOVERY_ADVICE}`);
        return;
      }
      onCapture(result.image, result.mimeType);
    } catch (err) {
      setScanError(err instanceof Error ? err.message : String(err));
    } finally {
      setScanning(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <TwainBridgeStatus status={twain.status} />
        {twain.status.status !== 'ready' && (
          <button
            type="button"
            onClick={() => twain.probe().catch(() => { /* ignore */ })}
            style={{
              ...BTN_PRIMARY,
              background: 'transparent',
              color: 'var(--pcb-blue)',
              border: '1px solid var(--pcb-blue)',
              padding: '4px 10px',
            }}
          >
            Retry
          </button>
        )}
      </div>
      {sources.length > 1 && (
        <div style={{ marginBottom: 10 }}>
          <label
            htmlFor="twain-source-select"
            style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-600)', display: 'block', marginBottom: 4 }}
          >
            Scanner source
          </label>
          <select
            id="twain-source-select"
            value={selected}
            onChange={(e) => twain.setSelectedSource(e.currentTarget.value)}
            style={{
              fontSize: 12,
              padding: '4px 8px',
              borderRadius: 4,
              border: '1px solid var(--surface-border)',
              minWidth: 220,
              fontFamily: "'Segoe UI', Roboto, sans-serif",
            }}
          >
            {sources.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      )}
      <button
        type="button"
        onClick={() => { void handleScan(); }}
        disabled={twain.status.status !== 'ready' || scanning}
        style={{
          ...BTN_PRIMARY,
          opacity: twain.status.status !== 'ready' || scanning ? 0.6 : 1,
          cursor: twain.status.status !== 'ready' ? 'not-allowed' : 'pointer',
        }}
      >
        {scanning ? 'Scanning…' : 'Scan Now'}
      </button>
      {scanError && <div style={ERROR_STYLE}>{scanError}</div>}
      {twain.status.status === 'missing' && (
        <div style={{ marginTop: 12 }}>
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 6,
              background: 'var(--info-bg)',
              border: '1px solid var(--surface-border)',
              fontSize: 12,
              color: 'var(--pcb-blue-dark)',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Scanner service not running?</div>
            <div style={{ lineHeight: 1.55, color: 'var(--ink-600)', marginBottom: 8 }}>
              Laptops that just connected to VPN may need a few seconds for the scanner service
              to come online. Click <em>Re-check scanner</em> if it&apos;s been more than 20 seconds.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => twain.probe().catch(() => { /* ignore */ })}
                style={{ ...BTN_PRIMARY, padding: '6px 12px' }}
              >
                Re-check scanner
              </button>
              <button
                type="button"
                onClick={() => {
                  // Why this pattern (vs. a plain <a href>): Teams' embedded
                  // WebView intercepts custom URL schemes (pcb-scanner://...)
                  // and tries to navigate inside the tab, blanking the view
                  // rather than delegating to the OS protocol handler.
                  //
                  // The hidden-iframe trick invokes the protocol handler
                  // via a request that the WebView can't navigate to
                  // (iframes don't change the parent URL), so Windows
                  // receives the pcb-scanner:// hand-off cleanly and fires
                  // the registered command (launcher.ps1 -ForceRestart).
                  // Works identically in Teams desktop, Teams web, and a
                  // plain PC browser.
                  //
                  // After ~2s we remove the iframe so it doesn't accumulate.
                  const iframe = document.createElement('iframe');
                  iframe.style.display = 'none';
                  iframe.src = 'pcb-scanner://restart';
                  document.body.appendChild(iframe);
                  window.setTimeout(() => {
                    try { document.body.removeChild(iframe); } catch { /* ignore */ }
                    // Re-probe the bridge after giving the launcher a
                    // moment to kill + respawn (Stop-Process 750 ms +
                    // Start-Process + TWAIN session init ~5 s).
                    twain.probe().catch(() => { /* ignore */ });
                  }, 6000);
                }}
                style={{
                  ...BTN_PRIMARY,
                  padding: '6px 12px',
                  background: 'transparent',
                  color: 'var(--pcb-blue)',
                  border: '1px solid var(--pcb-blue)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                title="Invokes the pcb-scanner://restart protocol handler registered on this PC, which kills and respawns the PCB Scanner Service. Handled by the OS — Teams does not navigate."
              >
                Restart scanner service
              </button>
            </div>
          </div>
          <details style={{ marginTop: 10, fontSize: 12, color: 'var(--ink-600)' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
              First-time branch PC setup (one-time)
            </summary>
            <ol style={{ margin: '6px 0 0 18px', lineHeight: 1.55 }}>
              <li>Open File Explorer to <code>F:\BT\PCBTwainBridge</code></li>
              <li>Double-click <strong>Install PCB Scanner Service</strong> (the PCB-branded launcher)</li>
              <li>Click <strong>Install</strong> in the installer window that appears</li>
              <li>Wait for all 5 green check marks</li>
            </ol>
            <div style={{ marginTop: 6, fontStyle: 'italic' }}>
              After install, the scanner starts automatically on every login and on every network reconnect.
            </div>
          </details>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Camera tab
// ---------------------------------------------------------------------------

interface CameraTabProps {
  onCapture: (base64: string, mimeType: string) => void;
}

function CameraTab({ onCapture }: CameraTabProps): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [streaming, setStreaming] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const stopCamera = (): void => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => {
        try { t.stop(); } catch { /* ignore */ }
      });
      streamRef.current = null;
    }
    setStreaming(false);
  };

  const startCamera = async (): Promise<void> => {
    setError(null);
    if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('Camera not supported in this browser. Use Upload instead.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try { await videoRef.current.play(); } catch { /* autoplay blocked — user retry */ }
        setStreaming(true);
      }
    } catch (err) {
      const name = err instanceof Error ? err.name : '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setError('Camera permission denied. Allow camera access in the browser and retry.');
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        setError('No camera found on this device. Use Upload instead.');
      } else {
        setError(err instanceof Error ? err.message : 'Camera failed to start.');
      }
    }
  };

  const capturePhoto = (): void => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError('Canvas 2D context unavailable.');
      return;
    }
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const base64 = dataUrl.substring(dataUrl.indexOf(',') + 1);
    onCapture(base64, 'image/jpeg');
    stopCamera();
  };

  // Cleanup on unmount.
  useEffect(() => {
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {!streaming ? (
        <div>
          <button type="button" onClick={() => { void startCamera(); }} style={BTN_PRIMARY}>
            Start Camera
          </button>
          {error && <div style={ERROR_STYLE}>{error}</div>}
        </div>
      ) : (
        <div>
          <video
            ref={videoRef}
            style={{ width: '100%', maxHeight: 360, borderRadius: 6, background: '#000' /* theme-exempt: camera viewport */ }}
            autoPlay
            playsInline
            muted
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" onClick={capturePhoto} style={BTN_PRIMARY}>Capture</button>
            <button type="button" onClick={stopCamera} style={BTN_DANGER}>Cancel</button>
          </div>
        </div>
      )}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload tab
// ---------------------------------------------------------------------------

interface UploadTabProps {
  onCapture: (base64: string, mimeType: string) => void;
  maxUploadBytes: number;
}

const ACCEPTED = '.jpg,.jpeg,.png,.gif,.bmp,.tiff,.tif,.pdf';

function fileToBase64Pair(file: File): Promise<{ base64: string; mimeType: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => {
      const dataUrl = reader.result as string;
      const commaIdx = dataUrl.indexOf(',');
      const base64 = commaIdx >= 0 ? dataUrl.substring(commaIdx + 1) : dataUrl;
      resolve({ base64, mimeType: file.type || 'application/octet-stream' });
    };
    reader.onerror = (): void => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function UploadTab({ onCapture, maxUploadBytes }: UploadTabProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const processFile = async (file: File): Promise<void> => {
    setError(null);
    // Intake accepts up to 25 MB (Bill 2026-07-13 Item 14); preflight
    // converts/downsamples (incl. oversize-PDF page-1 rasterize) so the
    // OUTPUT still honors the `maxUploadBytes` cap (Azure DI 4 MB).
    const INTAKE_MAX_BYTES = 25 * 1024 * 1024;
    if (file.size > INTAKE_MAX_BYTES) {
      setError(
        `File too large (max 25 MB). Got ${(file.size / 1024 / 1024).toFixed(1)} MB.`,
      );
      return;
    }
    try {
      const prepared =
        file.size > maxUploadBytes ? await preflightImageFile(file, maxUploadBytes) : file;
      const { base64, mimeType } = await fileToBase64Pair(prepared);
      onCapture(base64, mimeType);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to read file.');
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      void processFile(e.dataTransfer.files[0]!);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragging(false);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files && e.target.files.length > 0) {
      void processFile(e.target.files[0]!);
    }
  };

  // User-facing hint reflects the 25 MB INTAKE cap — preflight downsizes to
  // the pipeline cap (`maxUploadBytes`) automatically.
  const maxMb = '25';

  return (
    <div>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        style={{
          border: `2px dashed ${dragging ? 'var(--pcb-blue)' : 'var(--info-line)'}`,
          borderRadius: 8,
          padding: 30,
          textAlign: 'center',
          cursor: 'pointer',
          background: dragging ? 'var(--brand-tint-2)' : 'var(--info-bg)',
          color: dragging ? 'var(--pcb-blue)' : 'var(--ink-600)',
          transition: 'border-color 0.2s, background 0.2s',
        }}
      >
        <div style={{ fontSize: 28, marginBottom: 6 }} aria-hidden="true">📄</div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Drop a document here</div>
        <div style={{ fontSize: 12 }}>
          or click to browse — JPEG, PNG, TIFF, PDF (max {maxMb} MB)
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        title="Select document"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      {error && <div style={ERROR_STYLE}>{error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

function detectMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/Android|iPhone|iPad|iPod|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return true;
  if (ua.indexOf('Macintosh') >= 0 && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1) {
    return true;
  }
  return false;
}

export function DocumentCaptureModule(props: DocumentCaptureModuleProps): JSX.Element {
  const { onCapture, tabs, twain: externalTwain, maxUploadBytes = 4 * 1024 * 1024 } = props;
  const isMobile = useMemo(detectMobile, []);

  // Always call the hook (Rules of Hooks). When the caller passed a shared
  // instance (IdCapturePanel wiring), use it and discard the local one.
  // The duplicate hook is cheap — only the first probe runs a fetch on mount,
  // and React bails out of re-renders when state is identical.
  const localTwain = useTwainBridge();
  const twain: UseTwainBridgeResult = externalTwain ?? localTwain;

  // Figure out visible tabs.
  const visibleTabs = useMemo<ReadonlyArray<CaptureTab>>(() => {
    const requested = tabs ?? (['scanner', 'camera', 'upload'] as const);
    const out: CaptureTab[] = [];
    for (const t of requested) {
      if (t === 'scanner' && isMobile) continue; // Hide scanner on mobile.
      out.push(t);
    }
    return out.length > 0 ? out : (['upload'] as const);
  }, [tabs, isMobile]);

  const [active, setActive] = useState<CaptureTab>(() => visibleTabs[0] ?? 'upload');

  // If visibleTabs changes and the active one is no longer present, switch.
  useEffect(() => {
    if (!visibleTabs.includes(active)) {
      setActive(visibleTabs[0] ?? 'upload');
    }
  }, [visibleTabs, active]);

  const labels: Record<CaptureTab, string> = {
    scanner: 'Scanner',
    camera: 'Camera',
    upload: 'Upload',
  };

  return (
    <div style={SHELL_STYLE}>
      {visibleTabs.length > 1 && (
        <div role="tablist" style={TAB_BAR_STYLE}>
          {visibleTabs.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={active === t ? 'true' : 'false'}
              onClick={() => setActive(t)}
              style={tabStyle(active === t)}
            >
              {labels[t]}
            </button>
          ))}
        </div>
      )}
      <div style={BODY_STYLE}>
        {active === 'scanner' && <ScannerTab twain={twain} onCapture={onCapture} />}
        {active === 'camera' && <CameraTab onCapture={onCapture} />}
        {active === 'upload' && <UploadTab onCapture={onCapture} maxUploadBytes={maxUploadBytes} />}
      </div>
    </div>
  );
}
